<script setup lang="ts">
// NPM
import { ref, computed } from 'vue';
import { useMq } from 'vue3-mq';
import { useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

// Components
import {
  BaseCard,
  BaseEmptyState,
  Modal,
  BaseBadge,
  BaseButton,
  BaseLoader,
  BaseIcon,
} from '@magma-app/magma-lapilli';

// API
import { useEvents } from '@/api/events/use-events';
import { useRegisterEvent } from '@/api/events/use-register-event';
import { useActionDeepLink } from '@/composables/useActionDeepLink';

// Plugins
const mq = useMq();
const queryClient = useQueryClient();
const { locale } = useI18n();
const router = useRouter();

const { data: events, isLoading } = useEvents();
const { mutate: registerEvent, isPending: isRegistering } = useRegisterEvent();

// Reactive variables
const modal = ref<number | null>(null);
const currentModalStep = ref<'start' | 'confirm' | 'end'>('start');

// Opens one action's modal directly from a ?eventId= deep link.
useActionDeepLink({
  type: 'event',
  bucket: 'available',
  onOpen: (_item, id) => {
    modal.value = id;
    currentModalStep.value = 'start';
  },
});

const getEventImgUrl = (event: { pictureUrl?: string | null }) =>
  event.pictureUrl
    ? event.pictureUrl
    : mq.lgPlus
      ? '/img/event_lg.png'
      : '/img/event.png';

// Group events by month
const eventsByMonth = computed(() => {
  if (!events.value?.available.length) return [];

  const grouped = new Map<string, typeof events.value.available>();

  events.value.available.forEach((event) => {
    const eventDate = new Date(event.date);
    const monthKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;

    if (!grouped.has(monthKey)) {
      grouped.set(monthKey, []);
    }
    grouped.get(monthKey)!.push(event);
  });

  // Convert to array and sort by month (most recent first)
  return Array.from(grouped.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([monthKey, monthEvents]) => {
      const [year, month] = monthKey.split('-');
      const date = new Date(Number(year), Number(month) - 1);

      return {
        monthKey,
        monthLabel: capitalizeFirstLetter(
          date.toLocaleDateString(locale.value, {
            year: 'numeric',
            month: 'long',
          })
        ),
        events: monthEvents,
      };
    });
});

// Format date helper
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale.value, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

// Capitalize first letter
const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
</script>

<template>
  <div
    v-if="!isLoading"
    class="block h-full flex-1 flex-col gap-6 px-4 md:px-8"
  >
    <template v-if="eventsByMonth.length">
      <div
        v-for="monthGroup in eventsByMonth"
        :key="monthGroup.monthKey"
        class="mb-[18px] flex w-full flex-col gap-4"
      >
        <div class="flex flex-col gap-2">
          <p class="text-mgm-txt-md font-medium text-foreground-emphasis">
            {{ monthGroup.monthLabel }}
          </p>
          <div class="h-[1px] w-full border-t border-border-subtle"></div>
        </div>
        <div class="content-start flex flex-wrap gap-6">
          <Modal
            v-for="(event, eventIndex) in monthGroup.events"
            :key="eventIndex"
            :open="modal === event.eventId"
            content-class="max-w-[90dvw] sm:max-w-[480px]"
            @open-focus="(e) => e.preventDefault()"
            @open-change="
              (open) => {
                modal = event.eventId;

                if (!open) {
                  modal = null;
                  currentModalStep = 'start';
                }
              }
            "
          >
            <template #trigger>
              <BaseCard
                :title="event.name"
                :img-url="getEventImgUrl(event)"
                :points="event.points ?? undefined"
                :badge-status="{ points: event.points ?? 0, type: 'event' }"
                :display-points="!event.pictureUrl"
                :date="formatDate(event.date)"
              />
            </template>
            <template #content>
              <div v-if="!isLoading">
                <div
                  v-if="currentModalStep === 'start'"
                  class="flex flex-col gap-4"
                >
                  <img
                    :src="getEventImgUrl(event)"
                    :alt="event.name"
                    class="h-[200px] w-full rounded-[5px] object-cover"
                  />
                  <div class="flex flex-col gap-2">
                    <h3
                      class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
                    >
                      {{ event.name }}
                    </h3>
                    <div class="flex flex-row gap-4 items-center">
                      <div class="flex flex-row gap-2 items-center">
                        <BaseIcon icon="calendar" color="#5F6C85" />
                        <p class="text-mgm-txt-sm text-foreground-default">
                          {{ formatDate(event.date) }}
                        </p>
                      </div>
                      <BaseBadge
                        size="sm"
                        :text="event.points ?? 0"
                        icon-type="icon"
                        icon-name="star"
                      />
                    </div>
                  </div>
                  <div
                    class="rich-text-content text-mgm-txt-sm text-foreground-default max-h-[40dvh] lg:max-h-auto overflow-y-auto"
                    v-html="event.description"
                  ></div>
                  <BaseButton
                    size="lg"
                    class="w-full mt-2"
                    @click="currentModalStep = 'confirm'"
                  >
                    {{ $t('events.available.start.cta') }}
                  </BaseButton>
                </div>
                <div
                  v-if="currentModalStep === 'confirm'"
                  class="flex flex-col gap-4"
                >
                  <div
                    class="w-12 h-12 bg-[#F1F5F9] rounded-full border-8 border-[#F8FAFC] flex items-center justify-center"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                        stroke="#5F6C85"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <div class="flex flex-col gap-2">
                    <h3
                      class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
                    >
                      {{ $t('events.available.confirm.title') }}
                    </h3>
                    <p
                      class="text-mgm-txt-sm text-foreground-default overflow-hidden"
                    >
                      {{
                        $t('events.available.confirm.content', {
                          email: event.contactAdminEmail
                        })
                      }}
                    </p>
                  </div>
                  <div class="flex gap-2 mt-2">
                    <BaseButton
                      size="lg"
                      class="flex-1"
                      state="scdr"
                      :disabled="isRegistering"
                      @click="
                        () => {
                          currentModalStep = 'start';
                          modal = null;
                        }
                      "
                      >{{ $t('generic.cancel') }}</BaseButton
                    >
                    <BaseButton
                      size="lg"
                      class="flex-1"
                      :disabled="isRegistering"
                      :loading="isRegistering"
                      @click="
                        () => {
                          registerEvent(
                            { eventId: event.eventId },
                            {
                              onSuccess: () => {
                                currentModalStep = 'end';
                                queryClient.invalidateQueries({
                                  queryKey: ['events']
                                });
                              }
                            }
                          );
                        }
                      "
                      >{{ $t('events.available.confirm.cta') }}</BaseButton
                    >
                  </div>
                </div>
                <div
                  v-if="currentModalStep === 'end'"
                  class="flex flex-col gap-4"
                >
                  <div
                    class="w-12 h-12 bg-[#D1FADF] rounded-full border-8 border-[#ECFDF3] flex items-center justify-center"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999M22 3.99999L12 14.01L9.00001 11.01"
                        stroke="#03874C"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <div class="flex flex-col gap-2">
                    <h3
                      class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
                    >
                      {{ $t('events.available.end.title') }}
                    </h3>
                    <ul
                      class="list-disc pl-0 text-mgm-txt-sm text-foreground-default"
                    >
                      <li>
                        {{
                          $t('events.available.end.content.first', {
                            email: event.contactAdminEmail
                          })
                        }}
                      </li>
                      <li>
                        {{ $t('events.available.end.content.second') }}
                      </li>
                    </ul>
                  </div>
                  <BaseButton
                    size="lg"
                    class="w-full mt-2"
                    @click="
                      () => {
                        currentModalStep = 'start';
                        modal = null;
                        router.push({ name: 'ongoing-events' });
                      }
                    "
                    >{{ $t('generic.close') }}</BaseButton
                  >
                </div>
              </div>
              <BaseLoader v-else />
            </template>
          </Modal>
        </div>
      </div>
    </template>

    <BaseEmptyState
      v-else
      class="flex h-full flex-col items-center justify-center"
      :text="$t('eventsView.empty.all')"
      type="empty-list"
    />
  </div>
</template>
